'use client';

import { useRef } from 'react';

import { Banner } from '@flipster/apis/v1/banner/types';
import { FormattedMessage } from '@flipster/i18n';
import { Carousel } from '@flipster/react/design-system-legacy/carousel';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { Picture } from '@flipster/react/design-utils/picture';
import { css } from '@flipster/styled-system/css';
import { linkOverlay } from '@flipster/styled-system/patterns';
import { breakpointMB } from '@flipster/utils/constants/breakpoint';
// vip trial 시범 운영기간용 배너 이미지
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { maxWStyles, sectionSmallHeadingStyles } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';

interface Props {
  promotions?: Banner[];
}

const Promotions = ({ promotions }: Props) => {
  const intersectionRef = useRef<HTMLHeadingElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Promotions');

  if (!promotions?.length) return null;
  return (
    <Carousel
      spaceBetween={8}
      breakpoints={{ [breakpointMB]: { spaceBetween: 16 } }}
      slidesPerView='auto'
      withOverflow
      css={{ ...maxWStyles, py: { base: 48, tablet: 64 } }}
      title={
        <h2 ref={intersectionRef} className={css(sectionSmallHeadingStyles)}>
          <FormattedMessage id='Common.r251104.Promotions' />
        </h2>
      }
      navigator={<Carousel.Navigator css={{ tabletDown: { display: 'none' } }} />}
    >
      {promotions.map((b, index) => {
        return (
          <Carousel.Slide key={`item-${b.title}-${index}`} css={itemStyles}>
            <IsomorphicLink
              href={b.webLink || ''}
              className={linkOverlay()}
              tabIndex={-1}
              external
              onClick={() => {
                segment.track(MainEvent.MainPagePromotionCarouselBannerClick, {
                  button_url: b.webLink || '',
                  image_name: b.title || 'promotion image',
                });
              }}
            />
            <Picture
              src={{
                base: b.mobileImage + '?fm=webp&q=60',
                tablet: b.desktopImage + '?fm=webp&q=60',
              }}
              width={512}
              height={200}
              alt={b.title || 'promotion image'}
            />
          </Carousel.Slide>
        );
      })}
    </Carousel>
  );
};

export default Promotions;

const itemStyles = css.raw({
  pos: 'relative',
  overflow: 'hidden',
  rounded: { base: 8, tablet: 16 },
  w: { base: 292, tablet: 512 },
  h: { base: 134, tablet: 200 },
  mr: { base: 8, tablet: 16 },
  '& img': {
    w: '100%',
    h: 'auto',
  },
});
